import React, { useCallback, useEffect, useState } from "react";
import { Flask } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BTN_BLACK, BTN_WHITE, errText } from "@/components/pageSettings/AdminModal";

const LIVE = ["running", "pending"];
const PAIR = { min_views: 100, scope: "default", on_winner: "approve" };

// "Test these two": starts the overall thank-you design test with the two designs being compared.
export function TestPair({ left, right, names }) {
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/thankyou-test").then((r) => setT(r.data)).catch(() => setT(null)), []);
  useEffect(() => { load(); }, [load]);
  if (!t) return null;
  const live = LIVE.includes(t.status);
  const start = async (replace) => {
    if (replace && !window.confirm("Stop the running test without picking a winner, and start testing these two?")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/thankyou-test", { designs: [left, right], ...PAIR, replace });
      setT(r.data);
      toast.success(`Test started: ${names[left]} vs ${names[right]}`);
    } catch (e) {
      toast.error(errText(e, "Couldn't start the test"));
      load();
    } finally {
      setBusy(false);
    }
  };
  const running = (t.standings || []).map((s) => s.name).join(" vs ");
  const least = (t.standings || []).length ? Math.min(...t.standings.map((s) => s.views || 0)) : 0;
  const samePair = live && (t.designs || []).length === 2 && [left, right].every((k) => t.designs.includes(k)) && left !== right;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 border-2 border-black bg-yellow-50 p-2" data-testid="ty-compare-test">
      {live ? (
        <>
          <span className="text-xs" data-testid="ty-compare-test-running">
            <b>A design test is running:</b> {running} · {t.status === "pending" ? "winner waiting for your approval" : `${least} of ${t.min_views} views each so far`}
          </span>
          {samePair ? (
            <span className="ml-auto text-[11px] font-bold" data-testid="ty-compare-test-same-pair">You&apos;re already testing these two.</span>
          ) : left !== right && (
            <button type="button" disabled={busy} className={`${BTN_WHITE} ml-auto !px-2 !py-1 disabled:opacity-50`} onClick={() => start(true)} data-testid="ty-compare-test-replace-btn">
              Stop it and test these two
            </button>
          )}
        </>
      ) : left === right ? (
        <span className="text-xs" data-testid="ty-compare-test-same">Pick two different designs to test them.</span>
      ) : (
        <>
          <button type="button" disabled={busy} className={`${BTN_BLACK} !px-3 !py-1.5 disabled:opacity-50`} onClick={() => start(false)} data-testid="ty-compare-test-btn">
            <Flask size={12} weight="bold" /> Test these two
          </button>
          <span className="text-[11px] text-zinc-700" data-testid="ty-compare-test-note">
            Rotates {names[left]} and {names[right]} between visitors on pages using the default design. After 100 views each, the one with the most taps wins — you approve before it becomes the default.
          </span>
        </>
      )}
    </div>
  );
}
